import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useI18n } from '../hooks/useI18n';

interface LanguageSwitcherProps {
    className?: string;
    showLabel?: boolean;
}

const LANGUAGES = [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'hu', name: 'Hungarian', nativeName: 'Magyar' },
];

export function LanguageSwitcher({ className, showLabel = true }: LanguageSwitcherProps) {
    const { locale, changeLanguage, t } = useI18n();

    const handleLanguageChange = (newLocale: string) => {
        changeLanguage(newLocale);
    };

    const currentLanguage = LANGUAGES.find(lang => lang.code === locale);

    return (
        <div className={className}>
            {showLabel && (
                <Label htmlFor="language-select" className="text-sm font-medium">
                    {t('settings.language.title', 'common')}
                </Label>
            )}
            <Select value={locale} onValueChange={handleLanguageChange}>
                <SelectTrigger id="language-select" className="w-full">
                    <SelectValue>
                        {currentLanguage ? (
                            <span className="flex items-center gap-2">
                                <span className="text-lg">
                                    {locale === 'en' ? '🇺🇸' : '🇭🇺'}
                                </span>
                                {currentLanguage.nativeName}
                            </span>
                        ) : (
                            'Select language'
                        )}
                    </SelectValue>
                </SelectTrigger>
                <SelectContent>
                    {LANGUAGES.map((language) => (
                        <SelectItem key={language.code} value={language.code}>
                            <span className="flex items-center gap-2">
                                <span className="text-lg">
                                    {language.code === 'en' ? '🇺🇸' : '🇭🇺'}
                                </span>
                                <span>{language.nativeName}</span>
                                {language.code !== language.nativeName && (
                                    <span className="text-muted-foreground">({language.name})</span>
                                )}
                            </span>
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}

// Compact version for navigation/header
export function CompactLanguageSwitcher({ className }: { className?: string }) {
    const { locale, changeLanguage } = useI18n();

    const handleLanguageChange = (newLocale: string) => {
        changeLanguage(newLocale);
    };

    return (
        <Select value={locale} onValueChange={handleLanguageChange}>
            <SelectTrigger className={`w-20 h-8 ${className}`}>
                <SelectValue>
                    <span className="text-sm">
                        {locale === 'en' ? '🇺🇸' : '🇭🇺'}
                    </span>
                </SelectValue>
            </SelectTrigger>
            <SelectContent>
                {LANGUAGES.map((language) => (
                    <SelectItem key={language.code} value={language.code}>
                        <span className="flex items-center gap-1">
                            <span className="text-sm">
                                {language.code === 'en' ? '🇺🇸' : '🇭🇺'}
                            </span>
                            <span className="text-xs">{language.code.toUpperCase()}</span>
                        </span>
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}
